import { formatRecordDate as formatDate } from '../../utils/recordDate';
import React, { useMemo } from 'react';
import type { DynoSummary, PerformanceRecord } from '../../types/buildExpansion';
import { formatDynoPowerDisplay } from '../../utils/powerLabels';
import { VerificationChip } from './PerformanceRecords';
import { PerformanceSheetImage } from './PerformanceSheetImage';

/**
 * Dyno history: highlight cards (latest verified, highest wheel, highest
 * crank) plus a chronological progression table. Wheel vs crank is always
 * labeled. No chart library; each row carries a simple inline bar sized
 * relative to the highest power figure, with all values available as text.
 */


const measurementLabel = (location: PerformanceRecord['measurement_location']): string =>
  location === 'crank' ? 'Crank' : location === 'wheel' ? 'Wheel' : 'Power';

const formatPower = (record: PerformanceRecord): string => {
  return (
    formatDynoPowerDisplay(
      record.value_primary,
      record.unit_primary,
      record.measurement_location
    ) || '-'
  );
};

const formatTorque = (record: PerformanceRecord): string => {
  if (record.value_secondary == null) return '-';
  return `${record.value_secondary}${record.unit_secondary ? ` ${record.unit_secondary}` : ''}`;
};

interface HighlightCardProps {
  heading: string;
  record: PerformanceRecord;
}

const HighlightCard: React.FC<HighlightCardProps> = ({ heading, record }) => {
  const date = formatDate(record.date_recorded);
  const metaParts = [date, record.shop_name, record.dyno_type].filter(Boolean).join(' · ');
  return (
    <div className="rounded-lg border border-white/[0.06] bg-[#0a0a0a]/60 px-3.5 py-3">
      <p className="text-meta">{heading}</p>
      <div className="flex items-start gap-3 mt-1">
        <PerformanceSheetImage url={record.sheet_image_url} alt="Dyno graph" size="md" />
        <div className="min-w-0">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <span className="text-xl font-semibold text-white tabular-nums">{formatPower(record)}</span>
            <span className="text-[11px] font-medium uppercase tracking-wider text-gray-400 border border-white/[0.08] rounded px-1.5 py-0.5">
              {measurementLabel(record.measurement_location)}
            </span>
          </div>
          {record.value_secondary != null && (
            <p className="text-sm text-gray-400 mt-1 tabular-nums">{formatTorque(record)} torque</p>
          )}
          {metaParts && <p className="text-xs text-gray-500 mt-1">{metaParts}</p>}
          <div className="mt-2">
            <VerificationChip status={record.verification_status} />
          </div>
        </div>
      </div>
    </div>
  );
};

export interface DynoHistoryProps {
  summary: DynoSummary;
  className?: string;
}

export const countDynoRecords = (summary: DynoSummary | null | undefined): number => {
  if (!summary) return 0;
  const records = [
    ...(summary.history ?? []), summary.latest_verified, summary.highest_wheel, summary.highest_crank,
  ].filter((record): record is PerformanceRecord => record != null);
  return new Set(records.map(record => record.id)).size;
};

export const hasDynoContent = (summary: DynoSummary | null | undefined): boolean => countDynoRecords(summary) > 0;

export const DynoHistory: React.FC<DynoHistoryProps> = ({ summary, className = '' }) => {
  const history = useMemo(() => {
    const items = [...(summary?.history ?? [])];
    items.sort((a, b) => {
      const ta = a.date_recorded ? new Date(a.date_recorded).getTime() : 0;
      const tb = b.date_recorded ? new Date(b.date_recorded).getTime() : 0;
      return ta - tb;
    });
    return items;
  }, [summary]);

  const maxPower = useMemo(
    () =>
      history.reduce(
        (max, record) => (record.value_primary != null ? Math.max(max, record.value_primary) : max),
        0
      ),
    [history]
  );

  if (!hasDynoContent(summary)) return null;
  const highlights = [
    summary.latest_verified ? { heading: 'Latest verified', record: summary.latest_verified } : null,
    summary.highest_wheel ? { heading: 'Highest wheel', record: summary.highest_wheel } : null,
    summary.highest_crank ? { heading: 'Highest crank', record: summary.highest_crank } : null,
  ].filter((h): h is HighlightCardProps => h != null);

  if (highlights.length === 0 && history.length === 0) return null;

  return (
    <section className={`card p-5 sm:p-6 ${className}`} aria-label="Dyno history">
      <h3 className="build-section-heading text-sm font-semibold text-white">Dyno history</h3>

      {highlights.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5 mt-3">
          {highlights.map((h) => (
            <HighlightCard key={h.heading} heading={h.heading} record={h.record} />
          ))}
        </div>
      )}

      {history.length > 0 && (
        <div className="mt-5 overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <caption className="sr-only">
              Dyno progression in chronological order with power, torque, boost, fuel, tune, shop,
              and verification for each pull.
            </caption>
            <thead>
              <tr className="border-b border-white/[0.06]">
                <th scope="col" className="text-left text-meta font-medium py-2 pr-4">Date</th>
                <th scope="col" className="text-left text-meta font-medium py-2 pr-4">Power</th>
                <th scope="col" className="text-left text-meta font-medium py-2 pr-4">Torque</th>
                <th scope="col" className="text-left text-meta font-medium py-2 pr-4">Boost</th>
                <th scope="col" className="text-left text-meta font-medium py-2 pr-4">Fuel</th>
                <th scope="col" className="text-left text-meta font-medium py-2 pr-4">Tune</th>
                <th scope="col" className="text-left text-meta font-medium py-2 pr-4">Shop</th>
                <th scope="col" className="text-left text-meta font-medium py-2">Verification</th>
              </tr>
            </thead>
            <tbody>
              {history.map((record) => {
                const barPct =
                  maxPower > 0 && record.value_primary != null
                    ? Math.max(2, Math.min(100, (record.value_primary / maxPower) * 100))
                    : 0;
                const tune = [record.tune_revision, record.tuner_name].filter(Boolean).join(' · ');
                return (
                  <tr key={record.id} className="border-b border-white/[0.06] last:border-b-0 align-top">
                    <td className="py-2.5 pr-4 text-gray-400 whitespace-nowrap">
                      {formatDate(record.date_recorded) || '-'}
                    </td>
                    <td className="py-2.5 pr-4">
                      <span className="text-white font-medium tabular-nums whitespace-nowrap">
                        {formatPower(record)}
                        <span className="text-gray-500 font-normal">
                          {' '}
                          ({measurementLabel(record.measurement_location).toLowerCase()})
                        </span>
                      </span>
                      {barPct > 0 && (
                        <div className="h-1 w-full max-w-[140px] rounded-full bg-[#141414] overflow-hidden mt-1.5" aria-hidden>
                          <div
                            className="h-full rounded-full bg-orange-400/70"
                            style={{ width: `${barPct}%` }}
                          />
                        </div>
                      )}
                    </td>
                    <td className="py-2.5 pr-4 text-gray-300 tabular-nums whitespace-nowrap">
                      {formatTorque(record)}
                    </td>
                    <td className="py-2.5 pr-4 text-gray-300 tabular-nums whitespace-nowrap">
                      {record.boost_psi != null ? `${record.boost_psi} psi` : '-'}
                    </td>
                    <td className="py-2.5 pr-4 text-gray-300">{record.fuel || '-'}</td>
                    <td className="py-2.5 pr-4 text-gray-300">{tune || '-'}</td>
                    <td className="py-2.5 pr-4 text-gray-300">{record.shop_name || '-'}</td>
                    <td className="py-2.5">
                      <div className="flex flex-col items-start gap-2">
                        <VerificationChip status={record.verification_status} />
                        <PerformanceSheetImage url={record.sheet_image_url} alt="Dyno graph" />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default DynoHistory;
